import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GestionEmpresas({ user, onLogout }) {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, empresaId: null });
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    nombre_comercial: '',
    razon_social: '',
    cedula_juridica: '',
    direccion_completa: '',
    telefono: '',
    correo: '',
    representante_legal: '',
    cedula_representante: '',
  });
  const [esPrincipal, setEsPrincipal] = useState(false);
  const [cedulaImage, setCedulaImage] = useState(null);
  const [uploadingCedula, setUploadingCedula] = useState(false);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      const response = await axios.get(`${API}/empresas`);
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (empresa = null) => {
    if (empresa) {
      setEditingEmpresa(empresa);
      setFormData({
        nombre_comercial: empresa.nombre_comercial,
        razon_social: empresa.razon_social,
        cedula_juridica: empresa.cedula_juridica,
        direccion_completa: empresa.direccion_completa,
        telefono: empresa.telefono,
        correo: empresa.correo,
        representante_legal: empresa.representante_legal,
        cedula_representante: empresa.cedula_representante || '',
      });
      setEsPrincipal(empresa.es_principal);
      setCedulaImage(null);
    } else {
      setEditingEmpresa(null);
      setFormData({
        nombre_comercial: '',
        razon_social: '',
        cedula_juridica: '',
        direccion_completa: '',
        telefono: '',
        correo: '',
        representante_legal: '',
        cedula_representante: '',
      });
      setEsPrincipal(empresas.length === 0);
      setCedulaImage(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmpresa(null);
    setFormData({
      nombre_comercial: '',
      razon_social: '',
      cedula_juridica: '',
      direccion_completa: '',
      telefono: '',
      correo: '',
      representante_legal: '',
      cedula_representante: '',
    });
    setEsPrincipal(false);
    setCedulaImage(null);
  };

  const handleUploadCedula = async (empresaId) => {
    if (!cedulaImage) return null;

    setUploadingCedula(true);
    try {
      const formDataCedula = new FormData();
      formDataCedula.append('file', cedulaImage);

      const response = await axios.post(
        `${API}/empresas/${empresaId}/subir-cedula`,
        formDataCedula,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      return response.data.firma_url;
    } catch (error) {
      toast.error('Error al procesar la cédula: ' + (error.response?.data?.detail || 'Error desconocido'));
      return null;
    } finally {
      setUploadingCedula(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let empresaId;
      
      if (editingEmpresa) {
        await axios.put(`${API}/empresas/${editingEmpresa.id}?es_principal=${esPrincipal}`, formData);
        empresaId = editingEmpresa.id;
        toast.success('Empresa actualizada exitosamente');
      } else {
        const response = await axios.post(`${API}/empresas?es_principal=${esPrincipal}`, formData);
        empresaId = response.data.id;
        toast.success('Empresa creada exitosamente');
      }

      // Si hay imagen de cédula, subirla y extraer firma
      if (cedulaImage) {
        const firmaUrl = await handleUploadCedula(empresaId);
        if (firmaUrl) {
          toast.success('Firma extraída y guardada correctamente');
        }
      }

      handleCloseDialog();
      loadEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar empresa');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/empresas/${deleteDialog.empresaId}`);
      toast.success('Empresa eliminada exitosamente');
      setDeleteDialog({ open: false, empresaId: null });
      loadEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar empresa');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Cargando empresas...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl font-bold text-gray-900">Gestión de Empresas</h1>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>

        {/* Empresas List */}
        {empresas.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No hay empresas registradas</p>
              <Button onClick={() => handleOpenDialog()} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Crear primera empresa
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {empresas.map((empresa) => (
              <Card key={empresa.id} className={`border-2 ${empresa.es_principal ? 'border-emerald-500 bg-emerald-50' : ''}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg">{empresa.nombre_comercial}</span>
                    {empresa.es_principal && (
                      <Badge className="bg-emerald-600">
                        <Star className="w-3 h-3 mr-1" />
                        Principal
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p><strong>Razón Social:</strong> {empresa.razon_social}</p>
                    <p><strong>Cédula Jurídica:</strong> {empresa.cedula_juridica}</p>
                    <p><strong>Dirección:</strong> {empresa.direccion_completa}</p>
                    <p><strong>Teléfono:</strong> {empresa.telefono}</p>
                    <p><strong>Correo:</strong> {empresa.correo}</p>
                    <p><strong>Representante Legal:</strong> {empresa.representante_legal}</p>
                  </div>
                  
                  <div className="flex space-x-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenDialog(empresa)}
                      className="flex-1"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteDialog({ open: true, empresaId: empresa.id })}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
              </DialogTitle>
              <DialogDescription>
                Complete los datos de la empresa. Los datos se usarán en contratos y documentos.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nombre_comercial">Nombre Comercial *</Label>
                <Input
                  id="nombre_comercial"
                  value={formData.nombre_comercial}
                  onChange={(e) => setFormData({ ...formData, nombre_comercial: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="razon_social">Razón Social *</Label>
                <Input
                  id="razon_social"
                  value={formData.razon_social}
                  onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="cedula_juridica">Cédula Jurídica *</Label>
                <Input
                  id="cedula_juridica"
                  value={formData.cedula_juridica}
                  onChange={(e) => setFormData({ ...formData, cedula_juridica: e.target.value })}
                  placeholder="X-XXX-XXXXXX"
                  required
                />
              </div>

              <div>
                <Label htmlFor="direccion_completa">Dirección Completa *</Label>
                <Input
                  id="direccion_completa"
                  value={formData.direccion_completa}
                  onChange={(e) => setFormData({ ...formData, direccion_completa: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="correo">Correo Electrónico *</Label>
                <Input
                  id="correo"
                  type="email"
                  value={formData.correo}
                  onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="representante_legal">Representante Legal *</Label>
                <Input
                  id="representante_legal"
                  value={formData.representante_legal}
                  onChange={(e) => setFormData({ ...formData, representante_legal: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="cedula_representante">Cédula del Representante Legal *</Label>
                <Input
                  id="cedula_representante"
                  value={formData.cedula_representante}
                  onChange={(e) => setFormData({ ...formData, cedula_representante: e.target.value })}
                  placeholder="X-XXXX-XXXX"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Cédula del Representante (Imagen o PDF)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={(e) => setCedulaImage(e.target.files[0])}
                    disabled={uploadingCedula}
                  />
                  {cedulaImage && (
                    <Badge variant="outline" className="bg-green-50">
                      {cedulaImage.name}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Sube la imagen (JPG, PNG) o PDF de la cédula del representante legal. Se extraerá automáticamente la firma para los documentos.
                </p>
              </div>

              <div className="flex items-center space-x-2 pt-4 border-t">
                <input
                  type="checkbox"
                  id="es_principal"
                  checked={esPrincipal}
                  onChange={(e) => setEsPrincipal(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="es_principal" className="cursor-pointer">
                  Marcar como empresa principal
                </Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  {editingEmpresa ? 'Actualizar' : 'Crear'} Empresa
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, empresaId: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. No podrás eliminar la empresa si tiene trabajadores asignados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );
}
